import { Link } from "react-router-dom";
import { ArrowUpRight, Target, Gem, Compass } from "lucide-react";
import StatsRow from "../components/StatsCounter";
import { founders, leadership, stats } from "../lib/data";
import useSEO from "../lib/seo";

const PersonCard = ({ person, testId }) => (
  <div
    data-testid={testId}
    className="group relative overflow-hidden rounded-xl border border-white/10 bg-[#121212] hover:border-white/20 transition-all duration-300"
  >
    <div className="aspect-[4/5] overflow-hidden bg-[#1a1a1a] relative">
      <img
        src={person.image}
        alt={person.name}
        loading="lazy"
        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
    </div>
    <div className="p-6">
      <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#dc2626] mb-2">
        {person.role}
      </div>
      <div className="font-display font-bold text-xl text-white mb-3">
        {person.name}
      </div>
      <p className="text-[15px] text-neutral-100 leading-relaxed">{person.bio}</p>
    </div>
  </div>
);

export default function About() {
  useSEO({
    title: "About Us — Founding Partners & Leadership",
    description:
      "Meet the founding partners and leadership team of DVP DIGI WORLD. Decades of experience across banking, real estate, housing finance and LAP. ₹150 Cr+ disbursed across India.",
    path: "/about",
  });
  return (
    <div data-testid="page-about">
      {/* PAGE HERO */}
      <section className="relative border-b border-white/5">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1771270759486-1f7703945072?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBkYXJrfGVufDB8fHx8MTc3NjQ0Nzk0MXww&ixlib=rb-4.1.0&q=85')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/60 via-[#0a0a0a]/80 to-[#0a0a0a]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-28">
          <div className="text-xs font-bold tracking-[0.25em] uppercase text-[#dc2626]">
            About Us
          </div>
          <h1 className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-7xl tracking-tighter text-white max-w-4xl leading-[0.95]">
            Combining financial expertise with execution strength.
          </h1>
          <p className="mt-6 text-lg text-neutral-300 max-w-3xl leading-relaxed">
            DVP DIGI WORLD is a Chennai-based fintech and financial services
            company that helps individuals, businesses, SMEs and corporates
            access the right lending and protection products through strong
            institutional partnerships.
          </p>
        </div>
      </section>

      {/* STORY */}
      <section className="py-16 sm:py-24 lg:py-32 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-[#1d4ed8]">
              Our Story
            </div>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-white">
              End-to-end compliant, audit-ready &amp; customer-focused.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-neutral-300 leading-relaxed">
            <p>
            With our founding partners bringing decades of sector experience
              — including ₹150+ crores in Housing and LAP disbursements and
              30+ joint-venture real estate projects — DVP DIGI WORLD combines
              financial expertise with execution strength. The company focuses
              on simplifying access to capital, reducing turnaround times and
              delivering relevant financial products through a single trusted
              partner.
            </p>
            <p>
              Our positioning is grounded in phrases like{" "}
              <em className="text-white not-italic font-medium">
                end-to-end compliant, audit-ready, capital support, credit and
                ops resources
              </em>{" "}
              and <em className="text-white not-italic font-medium">quick decisioning</em> —
              aligned with modern fintech operating models while remaining
              deeply human in delivery.
            </p>
            <p>
              We work closely with banks, NBFCs and financial institutions to
              create a seamless experience for borrowers and business clients.
              By combining lender relationships, process expertise and strong
              operational support, we help customers move from enquiry to
              approval with greater clarity, efficiency and trust.
            </p>
          </div>
        </div>
      </section>

      {/* MISSION / VISION / VALUES */}
      <section className="py-16 sm:py-24 lg:py-32 border-b border-white/5 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon: Target,
              title: "Mission",
              text: "Simplify access to capital and protection for every customer segment through a trusted, unified partner.",
            },
            {
              icon: Compass,
              title: "Vision",
              text: "Be the most trusted unified financial partner across South India — combining fintech rigour with human guidance.",
            },
            {
              icon: Gem,
              title: "Values",
              text: "Transparency, compliance, speed, partnership and customer-first execution at every step of the journey.",
            },
          ].map((v, i) => (
            <div
              key={i}
              className="bg-[#121212] border border-white/10 rounded-xl p-8"
            >
              <div className="h-11 w-11 rounded-lg bg-[#dc2626]/10 border border-[#dc2626]/20 flex items-center justify-center text-[#dc2626] mb-5">
                <v.icon className="h-5 w-5" />
              </div>
              <h3 className="font-display font-bold text-2xl text-white mb-3">
                {v.title}
              </h3>
              <p className="text-neutral-400 leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FOUNDERS */}
      <section
        id="founders"
        className="py-16 sm:py-24 lg:py-32 border-b border-white/5"
        data-testid="section-founders"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <div className="text-xs font-bold tracking-[0.25em] uppercase text-[#dc2626]">
                Our Founding Partners
              </div>
              <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-6xl tracking-tighter text-white max-w-3xl">
                Two partners. <span className="text-neutral-500">Decades of expertise.</span>{" "}
                One unified vision.
              </h2>
            </div>
            <p className="text-neutral-400 max-w-sm leading-relaxed">
              Deep domain knowledge across banking, housing finance, LAP, real
              estate and project execution.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {founders.map((f) => (
              <PersonCard key={f.id} person={f} testId={`founder-card-${f.id}`} />
            ))}
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section
        id="leadership"
        className="py-16 sm:py-24 lg:py-32 border-b border-white/5 bg-[#0d0d0d]"
        data-testid="section-leadership"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <div className="text-xs font-bold tracking-[0.25em] uppercase text-[#1d4ed8]">
                Leadership Team
              </div>
              <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-6xl tracking-tighter text-white max-w-3xl">
                A leadership team <br />
                <span className="text-neutral-500">
                  backed by a young, dynamic workforce.
                </span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {leadership.map((l) => (
              <PersonCard key={l.id} person={l} testId={`leader-card-${l.id}`} />
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-16 sm:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <StatsRow items={stats} />
          <div className="mt-14 text-center">
            <Link
              to="/request-access"
              data-testid="about-cta"
              className="inline-flex items-center gap-2 bg-[#dc2626] hover:bg-[#ef4444] text-white px-6 py-4 font-semibold"
            >
              Work with us <ArrowUpRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
